import * as yup from "yup";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import AddDialogStyle from "styles/application/AddDialogStyle";
import { handleValidateObj, generateErrorState } from "helpers/utils";

// Init styled components
const ADD = AddDialogStyle();

// Yup validation schema
const schema = yup.object({
	name: yup
		.string("This field must be a string")
		.required("This field is required"),
	code: yup.string("This field must be a string").nullable(),
});

// Default state schemas
const defaultErrorSchema = { name: null, code: null };
const defaultStateSchema = { name: "", code: "" };

const AddDialog = ({
	open,
	closeHandler,
	applicationID,
	handleAddData,
	getError,
	subHeader,
	postAPI,
	isSiteAppSettingSystemPage,
}) => {
	// Init hooks
	const { t } = useTranslation();
	// Init state
	const [isUpdating, setIsUpdating] = useState(false);
	const [input, setInput] = useState(defaultStateSchema);
	const [errors, setErrors] = useState(defaultErrorSchema);
	// Handlers
	const closeOverride = () => {
		// Clearing input state and errors
		setInput(defaultStateSchema);
		setErrors(defaultErrorSchema);

		closeHandler();
	};
	const handleAddClick = async () => {
		// Adding progress indicator
		setIsUpdating(true);

		try {
			const localChecker = await handleValidateObj(schema, input);

			// Attempting API call if no local validaton errors
			if (!localChecker.some((el) => el.valid === false)) {
				// Creating new data
				const newData = await handleCreateData();

				if (newData.success) {
					setIsUpdating(false);
					closeOverride();
				} else {
					setErrors({ ...errors, ...newData.errors });
					setIsUpdating(false);
				}
			} else {
				const newErrors = generateErrorState(localChecker);

				setErrors({ ...errors, ...newErrors });
				setIsUpdating(false);
			}
		} catch (err) {
			// TODO: handle non validation errors here
			getError(err);

			setIsUpdating(false);
			closeOverride();
		}
	};
	const handleCreateData = async () => {
		// Attempting to create data
		try {
			const newData = await postAPI({
				siteAppID: applicationID,
				name: input.name,
				code: input.code,
			});

			// Handling success
			if (newData.status) {
				// Adding data to state
				handleAddData({
					id: newData.data,
					siteAppID: applicationID,
					name: input.name,
					code: input.code,
				});

				return { success: true };
			} else {
				if (newData.data.detail) {
					// getError(newData.data.detail);
					getError(t("message.alreadyExists", { name: subHeader }));
					return {
						success: false,
						errors: {
							name: null,
							code: null,
						},
					};
				} else {
					return { success: false, errors: { ...newData.data.errors } };
				}
			}
		} catch (err) {
			if (err.response.data.errors !== undefined) {
				setErrors({ ...errors, ...err.response.data.errors });
			} else {
				// If no explicit errors provided, throws to caller
				throw new Error(err);
			}

			return { success: false };
		}
	};

	const handleEnterPress = (e) => {
		// 13 is the enter keycode
		if (e.keyCode === 13) {
			handleAddClick();
		}
	};

	return (
		<div>
			<Dialog
				fullWidth={true}
				maxWidth="md"
				open={open}
				onClose={closeOverride}
				aria-labelledby="add-title"
				aria-describedby="add-description"
			>
				{isUpdating ? <LinearProgress /> : null}

				<ADD.ActionContainer>
					<DialogTitle id="alert-dialog-title">
						{<ADD.HeaderText>{t("title.addNew", { name: subHeader })}</ADD.HeaderText>}
					</DialogTitle>
					<ADD.ButtonContainer>
						<ADD.CancelButton onClick={closeOverride} variant="contained">
							{t("action.cancel")}
						</ADD.CancelButton>
						<ADD.ConfirmButton variant="contained" onClick={handleAddClick}>
							{t("action.addNew")}
						</ADD.ConfirmButton>
					</ADD.ButtonContainer>
				</ADD.ActionContainer>

				<ADD.DialogContent>
					<div>
						<ADD.InputContainer style={{ display: "flex", gap: "15px" }}>
							<ADD.NameInputContainer
								style={{ width: isSiteAppSettingSystemPage ? "50%" : "100%" }}
							>
								<ADD.NameLabel>
									{t("label.name")}<ADD.RequiredStar>*</ADD.RequiredStar>
								</ADD.NameLabel>
								<ADD.NameInput
									error={errors.name === null ? false : true}
									helperText={errors.name === null ? null : errors.name}
									variant="outlined"
									value={input.name}
									autoFocus
									onKeyDown={handleEnterPress}
									onChange={(e) => {
										setInput({ ...input, name: e.target.value });
									}}
								/>
							</ADD.NameInputContainer>{" "}
							{isSiteAppSettingSystemPage && (
								<ADD.NameInputContainer>
									<ADD.NameLabel>{t("label.code")}</ADD.NameLabel>
									<ADD.NameInput
										error={errors.code === null ? false : true}
										helperText={errors.code === null ? null : errors.code}
										variant="outlined"
										value={input.code}
										autoFocus
										onKeyDown={handleEnterPress}
										onChange={(e) => {
											setInput({ ...input, code: e.target.value });
										}}
									/>
								</ADD.NameInputContainer>
							)}
						</ADD.InputContainer>
					</div>
				</ADD.DialogContent>
			</Dialog>
		</div>
	);
};

export default AddDialog;
